/**
 * 总览。
 *
 * 一屏回答四个问题：
 *   规模多大 · 花了多少钱 · 数据健不健康 · 最近在涨还是在跌
 *
 * 「数据健康检查」放在最上面而不是最下面：每一项都对应一条设计红线或
 * 一个已知的旧系统缺陷，它比任何漂亮的数字都重要。
 */

import { Alert, Card, Col, Row, Space, Statistic, Table, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../../services/api";
import { fmtCents, fmtNumber, fmtPercent, StatusTag } from "../../components/format";

interface HealthCheck {
  key: string;
  label: string;
  detail: string;
  value: number;
  /**
   * 期望值。**null 表示这一项没有期望值**——它是存量事实不是待办，
   * 比如「旧系统遗留的未脱敏日记」：产品决定保留，所以不该报警，
   * 但要可见、要能看出有没有增长。
   */
  expected: number | null;
  severity: "ok" | "info" | "warning" | "critical";
}

interface Overview {
  users: {
    total: number;
    activeLast7d: number;
    newLast24h: number;
    newLast7d: number;
    newLast30d: number;
    byStatus: { status: string; count: number }[];
    identityProviders: { provider: string; count: number }[];
    adminGrants: number;
  };
  relationships: {
    total: number;
    bond: { avg: number | null; max: number | null; sum: number | null };
    characterAffinity: { avg: number | null; max: number | null };
    level: { avg: number | null; max: number | null };
    levelDistribution: { level: number; count: number }[];
    wallet: { power: number; lifetimeGranted: number; lifetimePurchased: number; lifetimeRefunded: number };
  };
  conversations: {
    conversations: number;
    messages: number;
    byRole: { role: string; count: number }[];
    fallbackCount: number;
    fallbackRate: number;
    feedback: { rating: number | null; count: number }[];
    firstMessageAt: string | null;
    lastMessageAt: string | null;
  };
  brain: {
    total: number;
    tokens: { input: number; cached: number; output: number; cacheHitRate: number };
    costCents: number;
    latency: { avgMs: number | null; maxMs: number | null };
    byForm: { formKey: string | null; count: number; costCents: number }[];
    byModel: { model: string; calls: number; inputTokens: number; outputTokens: number; costCents: number }[];
    bySafetyVerdict: { verdict: string; count: number }[];
    untrustedContentRuns: number;
  };
  memory: {
    conversationMemories: number;
    relationshipDiaries: number;
    characterDiaries: number;
    memoryIndexRows: number;
    deidentificationBreaches: number;
  };
  knowledge: { bases: number; versions: number; chunks: number };
  skills: { skillExecutions: number; toolExecutions: number; capabilityBindings: number };
  assets: { total: number; totalBytes: number };
  commerce: { revenueCents: number; paidOrders: number; pricingPolicyVersions: number };
  operations: { staleLeases: number; invalidSignatures: number; webhooks: number };
  safety: { total: number; byVerdict: { verdict: string; count: number }[] };
  migration: { total: number; byNewModel: { newModel: string; count: number }[] };
  audit: { total: number; last7d: number };
  generatedAt: string;
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2, ok: 3 } as const;

export function OverviewPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.get<Overview>("/api/v1/admin/overview"),
  });
  const health = useQuery({
    queryKey: ["overview", "health"],
    queryFn: () => api.get<HealthCheck[]>("/api/v1/admin/overview/health"),
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;

  const d = data;
  const checks = [...(health.data ?? [])].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );
  const problems = checks.filter((c) => c.severity !== "ok");

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {/* ---------------------------------------------------------- 健康检查 */}
      <Card
        title="数据健康检查"
        loading={health.isLoading}
        extra={
          problems.length === 0 ? (
            <Tag color="green">全部通过</Tag>
          ) : (
            <Tag color="orange">{problems.length} 项需要关注</Tag>
          )
        }
        styles={{ body: { padding: 12 } }}
      >
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={checks}
          columns={[
            {
              title: "",
              dataIndex: "severity",
              width: 90,
              render: (v: HealthCheck["severity"]) => <StatusTag value={v} />,
            },
            { title: "检查项", dataIndex: "label", width: 200 },
            {
              title: "实际 / 期望",
              width: 130,
              render: (_, r: HealthCheck) => (
                <span style={{ fontFamily: "monospace" }}>
                  {fmtNumber(r.value)}
                  {/* 没有期望值的项显示「—」而不是「null」。 */}
                  {" / "}
                  {r.expected === null ? "—" : fmtNumber(r.expected)}
                </span>
              ),
            },
            {
              title: "说明",
              dataIndex: "detail",
              render: (v: string) => <Typography.Text type="secondary">{v}</Typography.Text>,
            },
          ]}
        />
      </Card>

      {/* ------------------------------------------------------------ 规模 */}
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card loading={isLoading} styles={{ body: { padding: 16 } }}>
            <Statistic title="用户" value={d?.users.total ?? 0} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              近 7 日新增 {fmtNumber(d?.users.newLast7d)} · 活跃 {fmtNumber(d?.users.activeLast7d)}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading} styles={{ body: { padding: 16 } }}>
            <Statistic title="关系" value={d?.relationships.total ?? 0} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              平均羁绊 {Math.round(d?.relationships.bond.avg ?? 0)} · 最高等级{" "}
              {d?.relationships.level.max ?? 0}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading} styles={{ body: { padding: 16 } }}>
            <Statistic title="消息" value={d?.conversations.messages ?? 0} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {fmtNumber(d?.conversations.conversations)} 个会话 · 兜底率{" "}
              {fmtPercent(d?.conversations.fallbackRate, 3)}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card loading={isLoading} styles={{ body: { padding: 16 } }}>
            <Statistic title="中枢决策" value={d?.brain.total ?? 0} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              平均 {Math.round(d?.brain.latency.avgMs ?? 0)}ms · 失败见「中枢决策」页
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      {/* ------------------------------------------------------------ 成本 */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={12}>
          <Card title="模型成本" loading={isLoading} styles={{ body: { padding: 12 } }}>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <Statistic title="累计花费" value={fmtCents(d?.brain.costCents)} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="缓存命中率"
                  value={fmtPercent(d?.brain.tokens.cacheHitRate)}
                  valueStyle={{
                    color: (d?.brain.tokens.cacheHitRate ?? 0) < 0.3 ? "#B4232E" : "#2F7A4B",
                  }}
                />
              </Col>
              <Col span={8}>
                <Statistic title="输入 token" value={fmtNumber(d?.brain.tokens.input)} />
              </Col>
            </Row>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="缓存命中率是成本生死线"
              description="不启用上下文缓存的话，单轮成本约为启用后的 3.6 倍。迁移数据里该值为 0，因为旧系统没有记录缓存 token。"
            />
            <Table
              size="small"
              rowKey="model"
              pagination={false}
              dataSource={d?.brain.byModel ?? []}
              columns={[
                { title: "模型", dataIndex: "model" },
                { title: "调用", dataIndex: "calls", align: "right", render: fmtNumber },
                { title: "输入 token", dataIndex: "inputTokens", align: "right", render: fmtNumber },
                { title: "输出 token", dataIndex: "outputTokens", align: "right", render: fmtNumber },
                { title: "花费", dataIndex: "costCents", align: "right", render: fmtCents },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="灵力与经营" loading={isLoading} styles={{ body: { padding: 12 } }}>
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Statistic title="灵力存量" value={fmtNumber(d?.relationships.wallet.power)} />
              </Col>
              <Col span={8}>
                <Statistic title="累计赠送" value={fmtNumber(d?.relationships.wallet.lifetimeGranted)} />
              </Col>
              <Col span={8}>
                <Statistic title="累计充值" value={fmtNumber(d?.relationships.wallet.lifetimePurchased)} />
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}>
                <Statistic title="已付订单" value={d?.commerce.paidOrders ?? 0} />
              </Col>
              <Col span={8}>
                <Statistic title="收入" value={fmtCents(d?.commerce.revenueCents)} />
              </Col>
              <Col span={8}>
                <Statistic title="定价版本" value={d?.commerce.pricingPolicyVersions ?? 0} />
              </Col>
            </Row>
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
              旧系统没有付费，交易类表全部为空。结构已就绪，接入支付后无需改管理端。
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>

      {/* ------------------------------------------------------ 记忆与安全 */}
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={8}>
          <Card title="记忆与日记" loading={isLoading} styles={{ body: { padding: 12 } }}>
            <Row gutter={[8, 12]}>
              <Col span={12}>
                <Statistic title="会话摘要" value={d?.memory.conversationMemories ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="关系日记" value={d?.memory.relationshipDiaries ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="角色日记" value={d?.memory.characterDiaries ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="向量索引"
                  value={d?.memory.memoryIndexRows ?? 0}
                  valueStyle={{ color: (d?.memory.memoryIndexRows ?? 0) === 0 ? "#9A6413" : undefined }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="安全与审核" loading={isLoading} styles={{ body: { padding: 12 } }}>
            <Row gutter={[8, 12]}>
              <Col span={12}>
                <Statistic title="审核判定" value={d?.safety.total ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="不可信内容触发"
                  value={d?.brain.untrustedContentRuns ?? 0}
                  valueStyle={{ color: (d?.brain.untrustedContentRuns ?? 0) > 0 ? "#9A6413" : undefined }}
                />
              </Col>
              <Col span={12}>
                <Statistic title="审计记录" value={d?.audit.total ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="近 7 日审计" value={d?.audit.last7d ?? 0} />
              </Col>
            </Row>
            <div style={{ marginTop: 12 }}>
              {d?.safety.byVerdict.map((v) => (
                <Tag key={v.verdict} color={v.verdict === "block" ? "red" : v.verdict === "flag" ? "orange" : "green"}>
                  {v.verdict} {fmtNumber(v.count)}
                </Tag>
              ))}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="迁移与资产" loading={isLoading} styles={{ body: { padding: 12 } }}>
            <Row gutter={[8, 12]}>
              <Col span={12}>
                <Statistic title="ID 映射" value={d?.migration.total ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="资产" value={d?.assets.total ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="知识切片" value={d?.knowledge.chunks ?? 0} />
              </Col>
              <Col span={12}>
                <Statistic title="能力绑定" value={d?.skills.capabilityBindings ?? 0} />
              </Col>
            </Row>
            <Typography.Paragraph style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
              <Link to="/tables">查看全库 53 张表的行数与体积 →</Link>
            </Typography.Paragraph>
          </Card>
        </Col>
      </Row>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        统计时间 {d?.generatedAt ?? "—"}
      </Typography.Text>
    </Space>
  );
}
